<template>
    <div id="editproductioninfo">
        <ui5-button @click="save()">
            {{ trans('pages.edit_production_info.save') }}
        </ui5-button>
        <ui5-button @click="back()">
            {{ trans('pages.edit_production_info.cancel') }}
        </ui5-button>

        <div v-if="loadedData">
            <div class="target-times">
                <h2>{{ trans('pages.edit_production_info.target_times') }}</h2>
                <div
                    class="target-time"
                    :key="targetTimeIndex"
                    v-for="(targetTime, targetTimeIndex) in $v.targetTimes.$each
                        .$iter"
                >
                    <div>
                        <label>
                            {{
                                getWorkstationName(
                                    targetTime.workstation_id.$model
                                )
                            }}
                        </label>
                    </div>
                    <div>
                        <text-input
                            type="number"
                            v-model="targetTime.ideal_minutes.$model"
                            min="0"
                            step="1"
                        />
                        <div
                            v-if="
                                getErrorFromValidator(targetTime.ideal_minutes)
                            "
                            class="error"
                        >
                            {{
                                getErrorFromValidator(targetTime.ideal_minutes)
                            }}
                        </div>
                    </div>
                    <div>
                        <i
                            class="fa fa-trash-o"
                            @click="$delete(targetTimes, targetTimeIndex)"
                        />
                    </div>
                </div>

                <div
                    class="target-time add"
                    v-if="realWorkstationOptions.length"
                >
                    <div>
                        <ui5-select
                            @change="
                                selectedWorkstationId =
                                    $event.detail.selectedOption.value
                            "
                        >
                            <ui5-option
                                v-for="option in workstationTypeOptions"
                                :disabled="option.disabled"
                                :key="option.value"
                                :value="option.value"
                                :selected="
                                    option.value == selectedWorkstationId
                                "
                            >
                                {{ option.label }}
                            </ui5-option>
                        </ui5-select>
                    </div>
                    <div>
                        <text-input
                            type="number"
                            v-model="newIdealMinutes"
                            min="0"
                            step="1"
                        />
                    </div>
                    <div>
                        <ui5-button @click.stop="addMinutes()">+</ui5-button>
                    </div>
                </div>
            </div>

            <div style="clear: both" />
            <h2>{{ trans('pages.edit_production_info.production_info') }}</h2>

            <div class="field-container">
                <label for="capacityDefaultColor">
                    {{
                        trans(
                            'pages.edit_production_info.capacity_default_color'
                        )
                    }}
                    <small>
                        ({{ trans('pages.edit_production_info.in_days') }}):
                    </small>
                </label>
                <text-input
                    type="number"
                    v-model="productType.prodDaysForDefaultColor"
                    id="capacityDefaultColor"
                />
                <div
                    v-if="
                        getErrorFromValidator(
                            $v.productType.prodDaysForDefaultColor
                        )
                    "
                    class="error"
                >
                    {{
                        getErrorFromValidator(
                            $v.productType.prodDaysForDefaultColor
                        )
                    }}
                </div>
            </div>

            <div class="field-container">
                <label for="capacityNonDefaultColor">
                    {{
                        trans(
                            'pages.edit_production_info.capacity_non_default_color'
                        )
                    }}
                    <small>
                        ({{ trans('pages.edit_production_info.in_days') }}):
                    </small>
                </label>
                <text-input
                    type="number"
                    v-model="productType.prodDaysForNonDefaultColor"
                    id="capacityNonDefaultColor"
                />
                <div
                    v-if="
                        getErrorFromValidator(
                            $v.productType.prodDaysForNonDefaultColor
                        )
                    "
                    class="error"
                >
                    {{
                        getErrorFromValidator(
                            $v.productType.prodDaysForNonDefaultColor
                        )
                    }}
                </div>
            </div>

            <div class="field-container">
                <label for="productionMinutes">
                    {{
                        trans(
                            'pages.edit_production_info.total_production_time'
                        )
                    }}
                    <small>
                        ({{ trans('pages.edit_production_info.in_minutes') }}):
                    </small>
                </label>
                <text-input
                    type="number"
                    v-model="productType.productionMinutes"
                    id="productionMinutes"
                />
                <div
                    v-if="
                        getErrorFromValidator($v.productType.productionMinutes)
                    "
                    class="error"
                >
                    {{
                        getErrorFromValidator($v.productType.productionMinutes)
                    }}
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import ErrorHelper from '../helpers/ErrorHelper';
import { numeric, required, minValue } from 'vuelidate/lib/validators';
import productionTimesApi from '../api/productionTimes';
import productTypeApi from '../api/productType';
import workstationApi from '../api/workstation';
import { showErrorMessage, showInfoMessage } from '../dialogs';
import localization from '../mixins/localization';
import TextInput from '../components/TextInput.vue';

export default {
    components: { TextInput },
    data() {
        return {
            selectedWorkstationId: null,
            productTypeId: null,
            targetTimes: null,
            workstationTypes: null,
            productType: null,
            newIdealMinutes: 0,
        };
    },
    mixins: [localization],
    validations() {
        return {
            productType: {
                prodDaysForDefaultColor: {
                    numeric: numeric,
                    required: required,
                },
                prodDaysForNonDefaultColor: {
                    numeric: numeric,
                    required: required,
                },
                productionMinutes: {
                    numeric: numeric,
                    required: required,
                    minValue: minValue(1),
                },
            },
            targetTimes: {
                $each: {
                    producttype_id: {},
                    workstation_id: {},
                    ideal_minutes: {
                        numeric: numeric,
                        required: required,
                        minValue: minValue(1),
                    },
                },
            },
        };
    },
    computed: {
        loadedData() {
            return (
                this.targetTimes && this.workstationTypes && this.productType
            );
        },
        realWorkstationOptions() {
            return this.workstationTypeOptions.filter((x) => !x.disabled);
        },
        workstationTypeOptions() {
            if (!this.workstationTypes || this.workstationTypes.length == 0) {
                return [];
            }

            const used = this.targetTimes.map((x) =>
                parseInt(x.workstation_id)
            );

            const options = [];

            this.workstationTypes.forEach((workstationType) => {
                const relevantWorkstations =
                    workstationType.workstations.filter(
                        (workstation) => !used.includes(workstation.id)
                    );
                if (relevantWorkstations.length) {
                    options.push({
                        value: 't' + workstationType.id,
                        label: workstationType.externalName,
                        disabled: true,
                    });
                    relevantWorkstations.forEach((workstation) => {
                        options.push({
                            label: workstation.label,
                            value: workstation.id,
                            disabled: false,
                        });
                    });
                }
            });

            return options;
        },
    },
    methods: {
        back() {
            this.$router.push('/settingsProducttypes');
        },
        addMinutes() {
            const newItem = {
                workstation_id:
                    this.selectedWorkstationId ||
                    this.realWorkstationOptions[0].value,
                producttype_id: this.productTypeId,
                ideal_minutes: this.newIdealMinutes,
            };
            this.targetTimes.push(newItem);
            this.newIdealMinutes = 0;
            this.selectedWorkstationId = null;
            this.$forceUpdate();
        },
        save() {
            [this, ...Object.values(this.$refs).flat()].forEach((x) => {
                if (x.$v) {
                    x.$v.$touch();
                }
            });

            if (!this.isValid()) {
                showErrorMessage(
                    this.trans('pages.edit_production_info.error_resolve_input')
                );
            } else {
                const updateTimesPromise = productionTimesApi
                    .updateTargetTimes(this.productTypeId, this.targetTimes)
                    .then((response) => {
                        this.targetTimes = response.data.data;
                    })
                    .catch(() => {
                        showErrorMessage(
                            this.trans(
                                'pages.edit_production_info.error_unknown_save_target_times'
                            )
                        );
                    });

                const updateDaysPromise = productTypeApi.updateProductionDays(
                    this.productType.prodDaysForDefaultColor,
                    this.productType.prodDaysForNonDefaultColor,
                    this.productType.productionMinutes,
                    this.productTypeId
                );

                Promise.all([updateTimesPromise, updateDaysPromise]).then(
                    () => {
                        showInfoMessage(
                            this.trans(
                                'pages.edit_production_info.success_changes_saved'
                            )
                        );
                    }
                );
            }
        },
        getErrorFromValidator: ErrorHelper.getErrorFromValidator,
        getWorkstationName(id) {
            let workstation = null;
            if (this.workstationTypes) {
                this.workstationTypes.forEach((type) => {
                    if (type.workstations) {
                        type.workstations.forEach((station) => {
                            if (station.id == id) {
                                workstation = station;
                            }
                        });
                    }
                });
            }
            return workstation && workstation.label ? workstation.label : '?';
        },
        isValid() {
            let valid = true;
            [this, ...Object.values(this.$refs).flat()].forEach((x) => {
                if (x.$v && x.$v.$invalid) {
                    valid = false;
                }
            });
            return valid;
        },
    },
    mounted() {
        this.productTypeId = parseInt(this.$route.params.id);

        const promises = [
            productionTimesApi
                .fetchTargetTimesForProductType(this.productTypeId)
                .then((response) => {
                    this.targetTimes = response.data.data;
                }),
            workstationApi
                .fetchAllTypesWithWorkstations('externalName')
                .then((response) => {
                    this.workstationTypes = response.data.data;
                }),
            productTypeApi
                .productTypeDetails(this.productTypeId)
                .then((response) => {
                    this.productType = response.data.data;
                }),
        ];

        Promise.all(promises).then(() => {
            this.$store.commit(
                'general/setPageTitle',
                this.trans('pages.edit_production_info.page_title', {
                    label: this.productType.label,
                }),
                { root: true }
            );
        });
    },
};
</script>
